import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { notifyEvent } from '@/lib/notify'
import type { Project, ProjectMember, UserProfile } from '@/lib/types'
import { Avatar } from './Avatar'
import { cn, profileName } from '@/lib/utils'
import { useConfirm } from './ConfirmDialog'
import {
  X,
  UserPlus,
  Loader2,
  Crown,
  AlertCircle,
  Check,
  LogOut,
  UserMinus,
} from 'lucide-react'

interface Props {
  open: boolean
  project: Project | undefined
  currentUserId: string
  profilesById: Record<string, UserProfile>
  onClose: () => void
}

/** Manage who can collaborate on a project — invite by email, list, remove. */
export function ProjectMembersModal({
  open,
  project,
  currentUserId,
  profilesById,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Set when an invite succeeded but the email couldn't be delivered — the
  // owner shares this link manually.
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  // Invitee profile prefill (the invitee confirms it during onboarding).
  const [relation, setRelation] = useState<'internal' | 'external'>('internal')
  const [companyName, setCompanyName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const isOwner = !!project && project.user_id === currentUserId
  // The inviter's own company — used as the « same company » value.
  const ownerCompany = (profilesById[currentUserId]?.company_legal_name ?? '').trim()

  useEffect(() => {
    if (!open || !project) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setNotice(null)
    setInviteLink(null)
    setLinkCopied(false)
    setEmail('')
    setRelation('internal')
    setCompanyName('')
    setFirstName('')
    setLastName('')
    supabase
      .from('project_members')
      .select('*')
      .eq('project_id', project.id)
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) setError(error.message)
        else setMembers(data ?? [])
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, project])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !project) return null

  async function invite(e: React.FormEvent) {
    e.preventDefault()
    const addr = email.trim().toLowerCase()
    if (!addr || !project) return
    setInviting(true)
    setError(null)
    setNotice(null)
    try {
      const company = (relation === 'external' ? companyName : ownerCompany).trim()
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          project_id: project.id,
          email: addr,
          redirect_to: window.location.origin,
          company_legal_name: company || undefined,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
        },
      })
      if (error) {
        let detail = error.message
        try {
          const ctx = (error as { context?: Response }).context
          const body =
            ctx && typeof ctx.json === 'function' ? await ctx.json() : null
          if (body?.error) detail = body.error
        } catch {
          /* keep the generic message */
        }
        throw new Error(detail)
      }
      const res = data as {
        status?: string
        error?: string
        email_notified?: boolean
        invite_link?: string | null
      }
      if (res.error) throw new Error(res.error)
      const { data: fresh } = await supabase
        .from('project_members')
        .select('*')
        .eq('project_id', project.id)
      setMembers(fresh ?? [])
      setEmail('')
      // New invitee but the email didn't go out (e.g. provider trial cap) →
      // surface the invite link so the owner can share it manually.
      if (res.status === 'invited' && !res.email_notified && res.invite_link) {
        setInviteLink(res.invite_link)
        setNotice(null)
      } else {
        setInviteLink(null)
        setNotice(
          res.status === 'already_member'
            ? t('members.noticeAlreadyMember', { email: addr })
            : res.status === 'invited'
              ? t('members.noticeInvitationSent', { email: addr })
              : t('members.noticeAdded', { email: addr }),
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setInviting(false)
    }
  }

  async function removeMember(userId: string) {
    if (!project) return
    const self = userId === currentUserId
    const ok = await confirm({
      title: self
        ? t('members.confirmLeaveTitle')
        : t('members.confirmRemoveTitle'),
      message: self
        ? t('members.confirmLeaveMessage')
        : t('members.confirmRemoveMessage'),
      confirmLabel: self
        ? t('members.confirmLeaveButton')
        : t('members.confirmRemoveButton'),
    })
    if (!ok) return
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', project.id)
      .eq('user_id', userId)
    if (error) {
      setError(error.message)
      return
    }
    setMembers((m) => m.filter((x) => x.user_id !== userId))
    // Best-effort: notify the removed user. We don't send if they just
    // left voluntarily (no point emailing yourself "you left"). The
    // dispatcher also skips actor=recipient as a safety net.
    if (!self) {
      void notifyEvent({
        type: 'project_removed',
        project_id: project.id,
        removed_user_id: userId,
      })
    }
    if (self) onClose()
  }

  const ownerProfile = profilesById[project.user_id]

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/30 px-4 py-12 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-[var(--color-border-soft)] px-6 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold text-[var(--color-text-strong)]">
              {t('members.title', { name: project.name })}
            </h3>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-text-dim)]">
              {t(
                members.length + 1 === 1
                  ? 'members.personOne'
                  : 'members.personOther',
                { count: members.length + 1 },
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('members.closeAria')}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Member list */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <ul className="space-y-1.5">
            {/* Owner */}
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface-2)] px-3.5 py-2.5">
              <Avatar
                profile={ownerProfile}
                userId={project.user_id}
                className="h-8 w-8"
                textClassName="text-[11px]"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-[var(--color-text-strong)]">
                  {profileName(ownerProfile) || t('members.owner')}
                  {project.user_id === currentUserId && ` ${t('members.youSuffix')}`}
                </div>
                <div className="text-[11px] text-[var(--color-text-dim)]">
                  {t('members.owner')}
                </div>
              </div>
              <Crown className="h-4 w-4 shrink-0 text-amber-500" />
            </li>

            {loading && (
              <li className="flex items-center justify-center gap-2 py-4 text-[12px] text-[var(--color-text-dim)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('members.loading')}
              </li>
            )}

            {!loading &&
              members.map((m) => {
                const prof = profilesById[m.user_id]
                const self = m.user_id === currentUserId
                const pending = !prof?.last_seen_at
                const canRemove = isOwner || self
                return (
                  <li
                    key={m.user_id}
                    className="group flex items-center gap-3 rounded-2xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-3.5 py-2.5"
                  >
                    <Avatar
                      profile={prof}
                      userId={m.user_id}
                      className="h-8 w-8"
                      textClassName="text-[11px]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium text-[var(--color-text-strong)]">
                        {profileName(prof) || t('members.collaborator')}
                        {self && ` ${t('members.youSuffix')}`}
                      </div>
                      <div className="text-[11px] text-[var(--color-text-dim)]">
                        {pending
                          ? t('members.pendingInvite')
                          : t('members.collaborator')}
                      </div>
                    </div>
                    {canRemove && (
                      <button
                        type="button"
                        onClick={() => removeMember(m.user_id)}
                        title={
                          self
                            ? t('members.leaveProject')
                            : t('members.removeFromProject')
                        }
                        className="shrink-0 rounded-lg p-1.5 text-[var(--color-text-dim)] opacity-0 transition-all hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger)] group-hover:opacity-100"
                      >
                        {self ? (
                          <LogOut className="h-4 w-4" />
                        ) : (
                          <UserMinus className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </li>
                )
              })}

            {!loading && members.length === 0 && (
              <li className="py-3 text-center text-[12px] text-[var(--color-text-dim)]">
                {t('members.emptyState')}
              </li>
            )}
          </ul>
        </div>

        {/* Invite */}
        <div className="shrink-0 border-t border-[var(--color-border-soft)] px-6 py-4">
          {isOwner ? (
            <form onSubmit={invite} className="space-y-2.5">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium text-[var(--color-text-dim)]">
                  {t('members.inviteByEmail')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('members.inviteEmailPlaceholder')}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
              </div>

              {/* Same company or external? Drives the prefilled company. */}
              <div className="flex gap-1.5">
                {(['internal', 'external'] as const).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRelation(r)}
                    className={cn(
                      'flex-1 rounded-xl px-2.5 py-1.5 text-[12px] font-semibold transition-colors',
                      relation === r
                        ? 'bg-[var(--color-accent)] text-white'
                        : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
                    )}
                  >
                    {t(r === 'internal' ? 'members.relationInternal' : 'members.relationExternal')}
                  </button>
                ))}
              </div>

              {/* Company (raison sociale) — owner's company when internal,
                  free input when external. */}
              {relation === 'external' ? (
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t('members.companyPlaceholder')}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
              ) : ownerCompany ? (
                <div className="rounded-xl bg-[var(--color-surface-2)] px-3 py-2 text-[12px] text-[var(--color-text-dim)]">
                  {t('members.companyInternal', { company: ownerCompany })}
                </div>
              ) : null}

              {/* Optional name prefill (the invitee confirms it). */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('members.firstNamePlaceholder')}
                  className="min-w-0 flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('members.lastNamePlaceholder')}
                  className="min-w-0 flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-link)]"
                />
              </div>

              <div className="flex items-center justify-between gap-2">
                <p className="text-[10.5px] leading-snug text-[var(--color-text-dim)]">
                  {t('members.prefillHint')}
                </p>
                <button
                  type="submit"
                  disabled={inviting || !email.trim()}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {inviting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                  {t('members.inviteButton')}
                </button>
              </div>
            </form>
          ) : (
            <p className="text-[12px] text-[var(--color-text-dim)]">
              {t('members.ownerOnlyInvite')}
            </p>
          )}

          {error && (
            <div className="mt-2.5 flex items-start gap-2 rounded-xl border border-red-200 bg-[var(--color-danger-bg)] px-3 py-2 text-[11.5px] text-[var(--color-danger)]">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {notice && (
            <div className="mt-2.5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-[var(--color-done-bg)] px-3 py-2 text-[11.5px] text-emerald-800">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{notice}</span>
            </div>
          )}
          {inviteLink && (
            <div className="mt-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11.5px] text-amber-900">
              <div className="mb-1.5 font-medium">{t('members.inviteLinkNotice')}</div>
              <div className="flex items-center gap-1.5">
                <input
                  readOnly
                  value={inviteLink}
                  onFocus={(e) => e.currentTarget.select()}
                  className="min-w-0 flex-1 rounded-lg border border-amber-200 bg-white px-2 py-1 text-[11px] text-[var(--color-text)] outline-none"
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(inviteLink)
                      setLinkCopied(true)
                      setTimeout(() => setLinkCopied(false), 1500)
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="shrink-0 rounded-lg bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-amber-600"
                >
                  {linkCopied ? t('members.linkCopied') : t('members.copyLink')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
